import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { getAllCarsAction, searchCarsAction, filterListingAction, getAllFacetsAction } from '@/app/(public)/cars/actions';
import { parseSearchParams } from '@/utils/urlHelpers';
import { sortMapping } from '@/utils/SortingUtil';
import { CONTACT_TILE_INTERVAL, DEFAULT_PER_PAGE, type FacetGroup } from '@/types/cars';
export interface CarDataState {
  carData: any | null;
  isLoading: boolean;
  error: Error | null;
  totalItems: number;
  pagination: any;
}
interface UseCarDataOptions {
  contactDetails?: any;
  ipAddress?: string;
  shopOnline?: boolean;
  initialData?: any; // Initial car data from server
}

/**
 * Hook for managing car data with URL-based filtering
 * Replaces the complex logic from AccessingSRPData.js
 */
export function useCarData(options: UseCarDataOptions = {}) {
  const {
    contactDetails,
    ipAddress = '127.0.0.1',
    shopOnline = false,
    initialData
  } = options;
  const searchParams = useSearchParams();
  const [carData, setCarData] = useState<CarDataState>({
    carData: initialData || null,
    isLoading: !initialData,
    // Don't show loading if we have initial data
    error: null,
    totalItems: initialData?.pagination?.totalItems || 0,
    pagination: initialData?.pagination || null
  });
  const [facets, setFacets] = useState<FacetGroup | null>(null);

  // Refs for managing contact tile insertion
  const contactListIndexRef = useRef(0);

  // Parse current URL filters
  const filters = parseSearchParams(Object.fromEntries(searchParams.entries()));

  // Helper to check if any filters are active
  const hasActiveFilters = useCallback(() => {
    return !!(filters.make?.length || filters.model?.length || filters.bodystyle?.length || filters.year || filters.price || filters.mileage || filters.doors?.length || filters.cylinder?.length || filters.fuel?.length || filters.engine?.length || filters.train?.length || filters.transmission?.length || filters.incolor?.length || filters.excolor?.length || filters.q);
  }, [filters]);

  // Apply sorting parameters to query
  const applySorting = useCallback((query: any, sortOption?: string) => {
    if (!sortOption || !(sortMapping as any)[sortOption]) {
      query.orderBy = "createdAt";
      query.orderAsc = true;
      return;
    }
    const {
      orderBy,
      orderAsc
    } = (sortMapping as any)[sortOption];
    query.orderBy = orderBy;
    query.orderAsc = orderAsc;
  }, []);

  // Insert up to three marketing tiles into every fifth displayed slot.
  const insertContactTiles = useCallback((carsArray: any[]) => {
    if (!contactDetails?.contactList?.length) return carsArray;

    // Check if tiles are already present (injected by server)
    const hasExistingTiles = carsArray.some(item => {
      const doc = item?.document;
      return doc && !('vin' in doc) && ('contactNumber' in doc || 'highlightedText' in doc);
    });

    // If tiles are already present, don't inject again
    if (hasExistingTiles) {
      return carsArray;
    }
    let result = [...carsArray];
    let localContactIndex = contactListIndexRef.current;

    // Positions 5, 10, and 15 rotate naturally across a three-column grid.
    for (let i = 0; i < 3; i++) {
      const insertPosition = CONTACT_TILE_INTERVAL * (i + 1) - 1;
      if (result.length >= insertPosition) {
        if (localContactIndex >= contactDetails.contactList.length) {
          localContactIndex = 0;
        }
        const contactTile = {
          document: contactDetails.contactList[localContactIndex]
        };
        result = [...result.slice(0, insertPosition), contactTile, ...result.slice(insertPosition)];
        localContactIndex++;
      }
    }
    contactListIndexRef.current = localContactIndex;
    return result;
  }, [contactDetails]);

  // Build filter query string from current URL parameters
  const buildFilterQuery = useCallback(() => {
    const params = new URLSearchParams();

    // Add filter parameters
    if (filters.make?.length) {
      filters.make.forEach(make => params.append('make', make));
    }
    if (filters.model?.length) {
      filters.model.forEach(model => params.append('model', model));
    }
    if (filters.bodystyle?.length) {
      filters.bodystyle.forEach(style => params.append('bodystyle', style));
    }
    if (filters.year && (filters.year[0] !== 0 || filters.year[1] !== 0)) {
      params.append('year', filters.year[0].toString());
      params.append('year', filters.year[1].toString());
    }
    if (filters.price && (filters.price[0] !== 0 || filters.price[1] !== 0)) {
      params.append('price', filters.price[0].toString());
      params.append('price', filters.price[1].toString());
    }
    if (filters.mileage && (filters.mileage[0] !== 0 || filters.mileage[1] !== 0)) {
      params.append('mileage', filters.mileage[0].toString());
      params.append('mileage', filters.mileage[1].toString());
    }

    // Add other filter arrays
    ['doors', 'cylinder', 'fuel', 'engine', 'train', 'transmission', 'incolor', 'excolor'].forEach(key => {
      const values = filters[key as keyof typeof filters] as string[] | undefined;
      if (values?.length) {
        values.forEach(value => params.append(key, value));
      }
    });

    // Add sorting
    if (filters.sort) {
      const sortParams = sortMapping[filters.sort];
      if (sortParams) {
        params.append('orderBy', sortParams.orderBy);
        params.append('orderAsc', sortParams.orderAsc.toString());
      }
    }
    return params.toString();
  }, [filters]);

  // Fetch initial car data
  const fetchInitialData = useCallback(async () => {
    // Skip fetching if we already have initial data
    if (initialData) {
      return;
    }
    try {
      setCarData(prev_0 => ({
        ...prev_0,
        isLoading: true,
        error: null
      }));
      let data;
      if (filters.q) {
        // Search query
        const query_0 = {
          q: filters.q,
          page: 1,
          perPage: DEFAULT_PER_PAGE
        };
        if (filters.sort) {
          applySorting(query_0, filters.sort);
        }
        data = await searchCarsAction(query_0, shopOnline, ipAddress);
      } else if (hasActiveFilters()) {
        // Filter query
        const filterQuery = buildFilterQuery();
        data = await filterListingAction(filterQuery, DEFAULT_PER_PAGE, shopOnline, ipAddress);
      } else {
        // Default query - all cars
        const query_1 = {
          page: 1,
          perPage: DEFAULT_PER_PAGE
        };
        applySorting(query_1, filters.sort);
        data = await getAllCarsAction(query_1, shopOnline, ipAddress);
      }
      if (data) {
        // Insert contact tiles
        const carsWithTiles = insertContactTiles((data as any).items || (data as any).hits || []);
        setCarData({
          carData: {
            ...data,
            items: carsWithTiles
          },
          isLoading: false,
          error: null,
          totalItems: (data as any).pagination?.totalItems || 0,
          pagination: data.pagination
        });
      }
    } catch (error) {
      console.error('Error fetching car data:', error);
      setCarData(prev => ({
        ...prev,
        isLoading: false,
        error: error as Error
      }));
    }
  }, [initialData, filters, shopOnline, ipAddress, hasActiveFilters, buildFilterQuery, applySorting, insertContactTiles]);

  // Fetch updated facets when toggles change
  const refreshFacets = useCallback(async () => {
    try {
      const updatedFacets = await getAllFacetsAction(shopOnline, ipAddress);
      setFacets(updatedFacets as any);
    } catch (error_0) {
      console.error('Error fetching facets:', error_0);
    }
  }, [shopOnline, ipAddress]);

  // Effect to fetch data when filters change
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Effect to refresh facets when toggles change
  useEffect(() => {
    refreshFacets();
  }, [refreshFacets]);
  return {
    ...carData,
    facets,
    refreshData: fetchInitialData,
    hasActiveFilters: hasActiveFilters()
  };
}